import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {api} from '@/lib/api'
import type {Business} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

export type Notification = components['schemas']['NotificationRead']
type NotificationPage = components['schemas']['Page_NotificationRead_']
type NotificationKind = components['schemas']['NotificationKind']

export type NotificationGroup = {
  key: string
  kind: NotificationKind
  businessId: string | null
  items: Notification[]
}

function sameCalendarDay(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10)
}

// Solo se fusionan avisos ADYACENTES del mismo kind, empresa y día; un aviso suelto vuelve
// como grupo de uno para que el llamador pinte siempre la misma forma.
export function groupNotifications(notices: Notification[]): NotificationGroup[] {
  const groups: NotificationGroup[] = []
  for (const notice of notices) {
    const last = groups.at(-1)
    if (
      last &&
      last.kind === notice.kind &&
      last.businessId === (notice.business_id ?? null) &&
      sameCalendarDay(last.items[0].created_at, notice.created_at)
    ) {
      last.items.push(notice)
      continue
    }
    groups.push({
      key: `${notice.kind}:${notice.business_id ?? 'none'}:${notice.created_at.slice(0, 10)}:${notice.id}`,
      kind: notice.kind,
      businessId: notice.business_id ?? null,
      items: [notice],
    })
  }
  return groups
}

/** La vista previa es la primera página del feed (mismo tamaño que /notifications). Diez
 * se midió y no alcanzaba: un grupo ×10 se comía la página. */
const BELL_PREVIEW_SIZE = 20

// Sin latido a propósito: con el panel cerrado solo se mira el globito; la vista previa se
// refresca con el `refetch()` del clic que abre el panel.
export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () =>
      (await api.get<NotificationPage>('/notifications', {params: {page_size: BELL_PREVIEW_SIZE}})).data,
    enabled,
  })
}

/** `page_size: 1` porque del sobre solo se usa `total`; comparte el prefijo
 * `['notifications']`, así que marcar leído lo invalida. */
export function useUnreadNotificationsCount(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () =>
      (await api.get<NotificationPage>('/notifications', {params: {page_size: 1, unread_only: true}})).data.total,
    enabled,
    refetchInterval: 60_000,
  })
}

const NOTIFICATIONS_PAGE_SIZE = 20

export type NotificationsPageFilters = {
  kind?: NotificationKind
  unreadOnly?: boolean
}

// Clave propia: paginar aquí no toca la primera página que la campana tiene en caché.
export function useNotificationsPage(page: number, filters: NotificationsPageFilters = {}) {
  const {kind, unreadOnly} = filters
  return useQuery({
    queryKey: ['notifications', 'page', page, kind ?? null, unreadOnly ?? false],
    queryFn: async () =>
      (
        await api.get<NotificationPage>('/notifications', {
          params: {page, page_size: NOTIFICATIONS_PAGE_SIZE, kind, unread_only: unreadOnly || undefined},
        })
      ).data,
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['notifications']}),
  })
}

// Una sola petición con todos los ids: una por aviso agota el pool de conexiones de la API.
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => api.post('/notifications/read', {ids}),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['notifications']}),
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => api.post('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['notifications']}),
  })
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/notifications/${id}`),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['notifications']}),
  })
}

export function useDeleteReadNotifications() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => api.post('/notifications/delete-read'),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['notifications']}),
  })
}

// `SHARED_PATH` son los kinds que llegan al owner y al miembro asignado: enrutan por el rol.
const OWNER_ONLY_PATH: Partial<Record<NotificationKind, string>> = {
  arl_report_due: 'sgsst',
  designacion_vence: 'sgsst',
  cierre_ventana: 'sgsst',
  licencia_vence: 'employees',
  licencia_rechazada: 'employees',
  // Aterriza donde vive la descarga, nunca en la URL firmada.
  descarga_export_lista: 'sgsst',
  // Nunca en la URL firmada: allí se vuelve a pedir la compuerta de custodia.
  carpeta_visita_lista: 'sgsst/carpetas-visita',
  incidente_reportado: 'incidentes',
  furat_pendiente: 'incidentes',
  investigacion_vence: 'incidentes',
  territorial_vence: 'incidentes',
  investigacion_asignada: 'incidentes',
  incapacidad_reportada: 'sgsst/pendientes',
  // PQ-13: la bandeja de cambio de ocupación vive sobre la matriz de exámenes, no en Pendientes.
  cambio_ocupacion_sugerido: 'examenes',
}
const MEMBER_ONLY_PATH: Partial<Record<NotificationKind, string>> = {
  accion_asignada: 'mi/acciones',
  solicitud_asignada: 'mi/solicitudes',
}
const SHARED_PATH: Partial<Record<NotificationKind, {owner: string; member: string}>> = {
  accion_vencida: {owner: 'sgsst/plan', member: 'mi/acciones'},
  solicitud_por_vencer: {owner: 'sgsst', member: 'mi/solicitudes'},
  // `source_id` es la incapacidad, no el empleado: el operador aterriza en la cola.
  incapacidad_validada: {owner: 'sgsst/pendientes', member: 'mi/informacion?tab=incapacidades'},
}

function deepLinkFor(notice: Notification, myRole: string | undefined): string | null {
  const businessId = notice.business_id
  if (!businessId) return null
  const kind = notice.kind as NotificationKind
  // Los avisos de batería viven en el back-office y aterrizan en la página del lote
  // (`source_id`), nunca en una URL firmada.
  if ((kind === 'descarga_formas_lista' || kind === 'lectura_bateria_lista') && notice.source_id) {
    return `/admin/bateria/empresas/${businessId}/evaluaciones/${notice.source_id}`
  }
  // Con membresía viva (`source_id` = su ficha) va al perfil donde vive «Dar acceso»; sin
  // ella, a la nómina, donde primero hay que agregarla.
  if (kind === 'registro_bloqueado') {
    return notice.source_id
      ? `/businesses/${businessId}/empleados/${notice.source_id}`
      : `/businesses/${businessId}/employees`
  }
  // PQ-13: los destinatarios son los custodios de salud, que ya pueden ver de quién se trata.
  if ((kind === 'restriccion_por_vencer' || kind === 'cambio_ocupacion_sugerido') && notice.source_id) {
    return `/businesses/${businessId}/empleados/${notice.source_id}?tab=examenes`
  }
  const shared = SHARED_PATH[kind]
  const path = shared
    ? myRole === 'owner'
      ? shared.owner
      : shared.member
    : (OWNER_ONLY_PATH[kind] ?? MEMBER_ONLY_PATH[kind])
  return path ? `/businesses/${businessId}/${path}` : null
}

// El rol sale de `GET /businesses/{id}` (la misma query que `useBusiness`), no de la lista de
// membresías; el `staleTime` reutiliza esa entrada en vez de pedirla en cada clic.
const ROLE_STALE_MS = 5 * 60 * 1000

export function useNotificationNavigate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  return async (notice: Notification) => {
    let role: Business['role'] | undefined
    if (notice.business_id) {
      const business = await queryClient
        .fetchQuery({
          queryKey: ['business', notice.business_id],
          queryFn: async () => (await api.get<Business>(`/businesses/${notice.business_id}`)).data,
          staleTime: ROLE_STALE_MS,
        })
        .catch(() => null)
      role = business?.role
    }
    const path = deepLinkFor(notice, role)
    if (path) navigate(path)
  }
}
